// "Install on PCs missing it" (F-CAT-09, ADR-0056; spec/13 §9.1): from an app's library card menu, a centred confirm, then
// one request that asks every PC of the venue missing the app (of the classes the entry is offered to) to install it as
// soon as it is free, never during a session. A one-off: Install for players stays as it is. Says how many PCs were asked.

import type { LibraryEntry } from '@clavian/api-client';
import { useTranslation } from '@clavian/i18n/react';
import { Button, Dialog, useToast } from '@clavian/ui-web';
import { useState, type ReactNode } from 'react';

import { useProblem } from '../../api/use-problem';
import { Notice } from '../../ui';
import { useInstallLibraryEntry } from './queries';

export function InstallOnPcsDialog({
  organizationId,
  venueId,
  entry,
  name,
  onClose,
}: {
  readonly organizationId: string;
  readonly venueId: string;
  readonly entry: LibraryEntry;
  readonly name: string;
  readonly onClose: () => void;
}): ReactNode {
  const { t } = useTranslation('console');
  const { show } = useToast();
  const install = useInstallLibraryEntry(organizationId, venueId);
  const problem = useProblem();
  const [failed, setFailed] = useState<string | null>(null);

  const confirm = () => {
    setFailed(null);
    install.mutate(
      { entryId: entry.id },
      {
        onSuccess: (result) => {
          const asked = result.commands.length;
          show({ tone: 'ok', title: asked === 0 ? t('library.install.none', { app: name }) : t('library.install.sent', { count: asked, app: name }) });
          onClose();
        },
        onError: (failure) => setFailed(problem.text(failure, { fallback: t('library.install.failed', { app: name }) })),
      },
    );
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => (open ? undefined : onClose())}
      title={t('library.install.title', { app: name })}
      description={t('library.install.body', { app: name })}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" busy={install.isPending} onClick={confirm}>
            {t('library.install.confirm')}
          </Button>
        </>
      }
    >
      <p className="console-muted">{entry.installForPlayers ? t('library.install.switchOn') : t('library.install.switchOff')}</p>
      {failed !== null ? (
        <Notice tone="danger" role="alert">
          {failed}
        </Notice>
      ) : null}
    </Dialog>
  );
}
